import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  useToast,
  Text,
  Alert,
  AlertIcon,
  Code,
  HStack,
  Icon,
  Badge,
  Spinner,
} from '@chakra-ui/react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { FiMessageSquare, FiCopy } from 'react-icons/fi';
import DNCWarningAlert from '../DNCWarningAlert';
import SendTestSMSModal from '../Dialpad/SendTestSMSModal';
import DynamicVariableInsertButton from '../Email/DynamicVariableInsertButton';
import { buildVariableMap, resolveTemplateVariables, insertTagIntoTextarea, fetchDealTeamVariableContext } from '../../../utils/templateVariables';

export default function SMSTemplateModal({ isOpen, onClose, deal, contact }) {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const messageRef = useRef(null);
  const toast = useToast();
  const { currentUser } = useAuth();

  // Load SMS templates from Firestore
  useEffect(() => {
    const templatesRef = collection(db, 'smsTemplates');
    const q = query(templatesRef, where('isActive', '==', true));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const templatesList = [];
      snapshot.forEach((doc) => {
        templatesList.push({ id: doc.id, ...doc.data() });
      });
      setTemplates(templatesList);
      setLoadingTemplates(false);
    });

    return () => unsubscribe();
  }, []);

  // Deal-team context so {{owner.*}} / role tags preview like the backend send.
  const [dealTeam, setDealTeam] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const dealId = deal?.id || null;
    const dealOwnerId = deal?.ownerId || deal?.owner_id || null;
    const contactOwnerId = contact?.ownerId || contact?.owner_id || null;
    if (!dealId && !dealOwnerId && !contactOwnerId) {
      setDealTeam(null);
      return undefined;
    }
    fetchDealTeamVariableContext(dealId, { dealOwnerId, contactOwnerId })
      .then((ctx) => { if (!cancelled) setDealTeam(ctx); })
      .catch(() => { if (!cancelled) setDealTeam(null); });
    return () => { cancelled = true; };
  }, [deal?.id, deal?.ownerId, deal?.owner_id, contact?.ownerId, contact?.owner_id]);

  // Full variable map: every contact/deal field (flat + dot-notation) + sender.
  const variableMap = useMemo(() => buildVariableMap({
    contact,
    deal,
    sender: {
      name: currentUser?.displayName || currentUser?.email || '',
      email: currentUser?.email || '',
      phone: '',
    },
    dealTeam,
  }), [contact, deal, currentUser, dealTeam]);

  // While editing, unknown tags stay visible so nothing silently disappears;
  // finalizeMessage resolves again and strips leftovers at copy/send time.
  const fillTemplate = (text) => resolveTemplateVariables(text, variableMap, { stripUnknown: false });
  const finalizeMessage = (text) => resolveTemplateVariables(text, variableMap);

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setMessage(fillTemplate(template.body));
    }
  };

  const handleCopyPhone = () => {
    if (contact?.phone) {
      navigator.clipboard.writeText(contact.phone);
      toast({
        title: 'Phone copied',
        description: contact.phone,
        status: 'success',
        duration: 2000,
      });
    }
  };

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(finalizeMessage(message));
    toast({
      title: 'Message copied to clipboard',
      status: 'success',
      duration: 2000,
    });
  };

  const handleSendSMS = async () => {
    if (!message) {
      toast({
        title: 'Missing message',
        description: 'Please enter a message',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Resolve any remaining (manually typed) variables before copy/log
      const resolvedMessage = finalizeMessage(message);

      // Log activity to Supabase crm_activities
      if (isSupabaseConfigured() && deal?.id) {
        await supabase
          .from('crm_activities')
          .insert({
            deal_id: deal.id,
            author_id: currentUser.uid,
            type: 'sms_sent',
            content: `SMS prepared: ${resolvedMessage.substring(0, 200)}${resolvedMessage.length > 200 ? '...' : ''}`,
            metadata: {
              authorName: currentUser.displayName || currentUser.email,
              authorPhotoURL: currentUser.photoURL || '',
              recipientPhone: contact?.phone || '',
              recipientName: `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim(),
              templateUsed: selectedTemplate ? templates.find(t => t.id === selectedTemplate)?.name : null,
            },
          });
      }

      // Copy message to clipboard
      navigator.clipboard.writeText(resolvedMessage);

      toast({
        title: 'Message copied!',
        description: 'Paste into your messaging app. Activity logged to timeline.',
        status: 'success',
        duration: 4000,
      });

      // Reset and close
      setSelectedTemplate('');
      setMessage('');
      onClose();
    } catch (error) {
      console.error('Error preparing SMS:', error);
      toast({
        title: 'Error preparing SMS',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const charCount = message.length;
  const charColor = charCount > 160 ? 'orange' : charCount > 140 ? 'yellow' : 'gray';

  return (
  <>
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Icon as={FiMessageSquare} />
            <Text>Send SMS</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* DNC Warning - Blocks SMS */}
            {contact?.dnc && (
              <DNCWarningAlert 
                contactName={`${contact.firstName} ${contact.lastName}`}
                variant="full"
              />
            )}

            {contact?.phone ? (
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">
                  To: <Code>{contact.phone}</Code>
                </Text>
                <Button size="xs" variant="ghost" leftIcon={<FiCopy />} onClick={handleCopyPhone}>
                  Copy
                </Button>
              </HStack>
            ) : (
              <Alert status="warning" size="sm">
                <AlertIcon />
                <Text fontSize="sm">No phone number for this contact</Text>
              </Alert>
            )}

            <FormControl>
              <FormLabel fontSize="sm">Template</FormLabel>
              {loadingTemplates ? (
                <HStack p={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm">Loading templates...</Text>
                </HStack>
              ) : (
                <Select
                  placeholder="Select a template"
                  value={selectedTemplate}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  size="sm"
                >
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Select>
              )}
              <Text fontSize="xs" color="gray.500" mt={1}>
                All contact, deal, and sender variables are supported — use the insert
                button or type e.g. {'{{firstName}}, {{deal.loanAmount}}, {{loanOfficerName}}'}
              </Text>
            </FormControl>

            <FormControl isRequired>
              <HStack justify="space-between" align="center" mb={1}>
                <FormLabel fontSize="sm" mb={0}>
                  Message
                  <Badge ml={2} colorScheme={charColor}>
                    {charCount} chars
                  </Badge>
                </FormLabel>
                <DynamicVariableInsertButton
                  onInsert={(tag) => insertTagIntoTextarea(messageRef, message, tag, setMessage)}
                />
              </HStack>
              <Textarea
                ref={messageRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message..."
                size="sm"
                rows={6}
                maxLength={320}
              />
              {charCount > 160 && (
                <Text fontSize="xs" color="orange.600" mt={1}>
                  ⚠️ Message may be sent as multiple texts ({Math.ceil(charCount / 160)} parts)
                </Text>
              )}
            </FormControl>

            <Alert status="info" size="sm">
              <AlertIcon />
              <Text fontSize="xs">
                Message will be copied to clipboard. Paste into your messaging app to send.
              </Text>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            colorScheme="blue"
            onClick={() => setIsTestModalOpen(true)}
            isDisabled={!message || contact?.dnc}
            mr={2}
          >
            Send Test SMS
          </Button>
          <Button
            variant="outline"
            leftIcon={<FiCopy />}
            mr={2}
            onClick={handleCopyMessage}
            isDisabled={!message || contact?.dnc}
          >
            Copy
          </Button>
          <Button
            colorScheme="blue"
            leftIcon={<FiMessageSquare />}
            onClick={handleSendSMS}
            isLoading={isSubmitting}
            loadingText="Preparing..."
            isDisabled={!contact?.phone || !message || contact?.dnc}
          >
            Copy & Log
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    <SendTestSMSModal
      isOpen={isTestModalOpen}
      onClose={() => setIsTestModalOpen(false)}
      message={finalizeMessage(message)}
    />
  </>
  );
}

